import { DedicatedDriverPreferencesOutput } from '@allocab/allocab-dedicated-driver-types';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { DriverPreferencesDiagram } from './DriverPreferencesDiagram';
import { DriverPreferencesOfDay } from './DriverPreferencesOfDay';
import { FormValues } from './types';
import { formatDriverPreferenceInput, timeOptions, weekDays } from './utils';
import { SelectInput } from '../../components/atomic/SelectInput';
import { GradientButton } from '../../components/button/GradientButton';
import { useCreateDriverPreferences } from '../../hooks/useCreateDriverPreferences';
import { t } from '../../services/translation/translation';

type Props = {
  driverPreferenceData: DedicatedDriverPreferencesOutput;
};

export const DriverFormPreferences = ({ driverPreferenceData }: Props) => {
  const { driverKey } = useParams();

  invariant(driverKey);

  const { mutate: createDedicatedDriverPreferences, isLoading: isCreatingDedicatedPreferences } =
    useCreateDriverPreferences(driverKey);

  const form = useForm<FormValues>({ defaultValues: { startTime: '00:00', endTime: '00:00' } });

  const handleSubmit = form.handleSubmit(value => {
    createDedicatedDriverPreferences(formatDriverPreferenceInput(value), {});
  });

  return (
    <form onSubmit={handleSubmit}>
      <div className="block min-w-full sm:min-w-0 p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
        <h6 className="text-lg font-bold dark:text-white mb-2">{t('driver-preferences-route.availabilities')}</h6>

        <div className="space-y-3 lg:space-y-0 lg:flex gap-4 mt-4">
          <div className="flex-1 relative">
            <div className="lg:sticky lg:top-3 space-y-2">
              <div className="flex flex-row items-end gap-2">
                <SelectInput
                  {...form.register('day', { required: true })}
                  options={weekDays}
                  defaultValue="monday"
                  label={t('common.day')}
                />
                <SelectInput
                  {...form.register('startTime', { required: true })}
                  options={timeOptions}
                  label={t('common.start')}
                />
                <SelectInput
                  {...form.register('endTime', { required: true })}
                  options={timeOptions}
                  defaultValue="0"
                  label={t('common.end')}
                />

                <div className="flex items-center justify-center lg:justify-normal">
                  <GradientButton isLoading={isCreatingDedicatedPreferences} type="submit">
                    {t('common.add')}
                  </GradientButton>
                </div>
              </div>
              <DriverPreferencesDiagram driverPreferenceData={driverPreferenceData} />
            </div>
          </div>
          <div className="space-y-3 lg:order-first">
            <DriverPreferencesOfDay
              day={{ label: t('common.monday'), value: 'monday' }}
              slots={driverPreferenceData.preferences.monday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.tuesday'), value: 'tuesday' }}
              slots={driverPreferenceData.preferences.tuesday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.wednesday'), value: 'wednesday' }}
              slots={driverPreferenceData.preferences.wednesday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.thursday'), value: 'thursday' }}
              slots={driverPreferenceData.preferences.thursday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.friday'), value: 'friday' }}
              slots={driverPreferenceData.preferences.friday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.saturday'), value: 'saturday' }}
              slots={driverPreferenceData.preferences.saturday}
              driverKey={driverKey}
            />
            <DriverPreferencesOfDay
              day={{ label: t('common.sunday'), value: 'sunday' }}
              slots={driverPreferenceData.preferences.sunday}
              driverKey={driverKey}
            />
          </div>
        </div>
      </div>
    </form>
  );
};
